/**
 * Homepage Editor - Comprehensive Homepage Content Management
 * 
 * Allows Tammy to edit ALL homepage content:
 * - Hero section (title, description, badge, images)
 * - Features section ("Why Choose Nanny B's Bakery?")
 * - Section titles and descriptions
 * - CTA sections
 * - SEO fields
 * 
 * Future-proofed with advanced features support.
 */

import { useState, useEffect, useRef } from 'react';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiEye,
  HiEyeOff,
  HiRefresh,
  HiUpload,
  HiX,
  HiSave,
  HiPhotograph,
  HiSparkles,
  HiDocumentText,
  HiTag,
  HiGlobe,
} from 'react-icons/hi';
import Tooltip from '../../components/Tooltip';
import ImageGalleryPicker from '../components/ImageGalleryPicker';
import ImageSelector from '../components/ImageSelector';
import { adminFetch, adminHeaders } from '../lib/adminApi';
import {
  DEFAULT_FEATURES_SECTION,
  DEFAULT_KITCHEN_GALLERY,
  DEFAULT_TESTIMONIALS,
  DEFAULT_CAKE_QUOTE,
  normalizeTestimonialsItems,
  type HomepageTestimonialItem,
} from '../../utils/homepageSectionDefaults';

// ===========================================
// Types
// ===========================================

interface HomepageFeature {
  id: string;
  title: string;
  description: string;
  image_url: string;
  icon_name: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface HomepageContent {
  id: string;
  // Hero
  hero_badge_text: string;
  hero_title: string;
  hero_description: string;
  hero_image_url: string;
  hero_floating_product_image_url: string;
  hero_floating_product_text: string;
  /** Animated cookie/cake/cupcake decorations on the welcome banner */
  hero_floating_icons_visible?: boolean;
  /** Small product photo card on the welcome banner */
  hero_floating_product_visible?: boolean;
  // Featured Products
  featured_products_title: string;
  featured_products_description: string;
  featured_products_visible: boolean;
  // Popular Treats
  popular_treats_title: string;
  popular_treats_description: string;
  popular_treats_visible: boolean;
  // Custom Cakes
  custom_cakes_title: string;
  custom_cakes_description: string;
  custom_cakes_notice_text: string;
  custom_cakes_button_text: string;
  custom_cakes_visible: boolean;
  /** Why Choose section heading (cards stay on Features tab list below) */
  features_section_title?: string;
  features_section_description?: string;
  features_section_visible?: boolean;
  /** Fresh from Our Kitchen gallery copy (photos: Gallery → Featured) */
  kitchen_gallery_title?: string;
  kitchen_gallery_description?: string;
  kitchen_gallery_button_text?: string;
  kitchen_gallery_visible?: boolean;
  /** Customer testimonials */
  testimonials_title?: string;
  testimonials_description?: string;
  testimonials_visible?: boolean;
  testimonials_items?: HomepageTestimonialItem[];
  /** "Want a Custom Cake?" quote above pricing / call buttons */
  cake_quote_title?: string;
  cake_quote_description?: string;
  cake_quote_notice?: string;
  // Custom Cake Pricing (shown on home page "Want a Custom Cake?" section)
  cake_pricing_visible: boolean;   // when true, show the price cards; when false, show call/message CTA
  cake_price_6inch: string | null; // e.g. "Starting at $65" or "$55–$95"
  cake_price_8inch: string | null;
  cake_price_2tier: string | null;
  cake_price_3tier: string | null;
  cake_pricing_note: string | null; // optional note below the prices
  // Fundraiser spotlight — Tammy can show/hide the Live Fundraisers section on homepage
  fundraisers_visible: boolean;
  // Social proof counter bar — shows 4 stat boxes (cakes made, ratings, etc.)
  social_proof_visible:       boolean;
  social_proof_stat_1_number: string;
  social_proof_stat_1_label:  string;
  social_proof_stat_2_number: string;
  social_proof_stat_2_label:  string;
  social_proof_stat_3_number: string;
  social_proof_stat_3_label:  string;
  social_proof_stat_4_number: string;
  social_proof_stat_4_label:  string;
  // CTA
  cta_section_title: string;
  cta_section_description: string;
  cta_button_text: string;
  cta_phone_button_text: string;
  cta_section_visible: boolean;
  // SEO
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  og_image_url: string | null;
}

type TabType = 'hero' | 'features' | 'sections' | 'ctas' | 'seo' | 'site-images';

// API base URL so admin requests hit the correct backend (fixes feature image save when frontend and backend differ)
const API_URL = import.meta.env.VITE_API_URL || '';

// ===========================================
// Constants
// ===========================================

const ICON_OPTIONS = [
  { value: 'CakeIcon', label: '🎂 Cake' },
  { value: 'ShoppingBagIcon', label: '🛍️ Shopping Bag' },
  { value: 'HeartIcon', label: '❤️ Heart' },
  { value: 'StarIcon', label: '⭐ Star' },
  { value: 'SparklesIcon', label: '✨ Sparkles' },
];

const emptyFeatureForm: Omit<HomepageFeature, 'id' | 'created_at' | 'updated_at'> = {
  title: '',
  description: '',
  image_url: '',
  icon_name: 'CakeIcon',
  is_active: true,
  display_order: 0,
};

/** Site image override keys and default URLs; Tammy can set an override for any of these. */
const SITE_IMAGE_KEYS: Array<{ key: string; label: string; defaultUrl: string }> = [
  { key: 'logo', label: 'Logo (header, footer, favicon)', defaultUrl: '/assets/images/branding/logo2.webp' },
  { key: 'hero_image', label: 'Hero image (storefront)', defaultUrl: '/assets/images/exterior/storefront-exterior.webp' },
  { key: 'hero_floating_product_image', label: 'Hero floating product image', defaultUrl: '/assets/images/products/product-chocolate-cake-ganache-ferrero-rocher.webp' },
  { key: 'og_image', label: 'Default OG/social share image', defaultUrl: '/assets/images/exterior/storefront-exterior.webp' },
  { key: 'placeholder_event', label: 'Event banner placeholder', defaultUrl: '/assets/images/branding/logo2.webp' },
  { key: 'placeholder_product', label: 'Product/menu placeholder', defaultUrl: '/assets/images/products/product-image-2.webp' },
  { key: 'placeholder_menu', label: 'Menu item fallback placeholder', defaultUrl: '/assets/images/placeholder.webp' },
  { key: 'feature_custom_cakes', label: '"Custom Cakes" feature card image', defaultUrl: '/assets/images/products/product-birthday-cake-happy-birthday-topper-sprinkles.webp' },
  { key: 'feature_fresh_daily', label: '"Fresh Daily" feature card image', defaultUrl: '/assets/images/products/product-donuts-cinnamon-sugar.webp' },
  { key: 'feature_made_with_love', label: '"Made with Love" feature card image', defaultUrl: '/assets/images/products/product-chocolate-covered-strawberries.webp' },
  { key: 'schema_logo', label: 'Structured data / schema logo', defaultUrl: 'https://nannybbakery.ca/assets/images/branding/logo2.webp' },
  { key: 'schema_placeholder', label: 'Structured data product fallback image', defaultUrl: 'https://nannybbakery.ca/assets/images/exterior/storefront-exterior.webp' },
];

// ===========================================
// Main Component
// ===========================================

export default function HomepageEditor() {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('hero');
  
  // Homepage content state
  const [content, setContent] = useState<HomepageContent | null>(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentSaving, setContentSaving] = useState(false);
  
  // Features state (from existing HomepageFeatures)
  const [features, setFeatures] = useState<HomepageFeature[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [featureForm, setFeatureForm] = useState(emptyFeatureForm);
  const [featureSaving, setFeatureSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Error state
  const [error, setError] = useState<string | null>(null);

  // Site image overrides (for "Site Images" tab)
  const [siteOverrides, setSiteOverrides] = useState<Record<string, string>>({});
  const [siteOverridesLoading, setSiteOverridesLoading] = useState(false);
  const [siteOverridesSaving, setSiteOverridesSaving] = useState(false);
  const [siteOverrideForm, setSiteOverrideForm] = useState<Record<string, string>>({});

  // ===========================================
  // Fetch Functions
  // ===========================================

  // Fetch homepage content
  const fetchContent = async () => {
    setContentLoading(true);
    setError(null);
    try {
      const response = await adminFetch(`${API_URL}/api/homepage-content`);
      if (!response.ok) throw new Error('Failed to fetch homepage content');
      const data = await response.json();
      setContent(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load homepage content');
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch features
  const fetchFeatures = async () => {
    setFeaturesLoading(true);
    setError(null);
    try {
      const response = await adminFetch(`${API_URL}/api/homepage-features`);
      if (!response.ok) throw new Error('Failed to fetch features');
      const data = await response.json();
      setFeatures(data.features || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load features');
    } finally {
      setFeaturesLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
    fetchFeatures();
  }, []);

  // Fetch site image overrides when opening Site Images tab
  const fetchSiteOverrides = async () => {
    setSiteOverridesLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/site-image-overrides`);
      if (response.ok) {
        const data = await response.json();
        const overrides = data.overrides || {};
        setSiteOverrides(overrides);
        setSiteOverrideForm(overrides);
      }
    } catch (err) {
      console.warn('Failed to fetch site image overrides:', err);
      setSiteOverrides({});
      setSiteOverrideForm({});
    } finally {
      setSiteOverridesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'site-images') {
      fetchSiteOverrides();
    }
  }, [activeTab]);

  const saveSiteOverride = async (key: string, url: string) => {
    setSiteOverridesSaving(true);
    setError(null);
    try {
      const response = await adminFetch(`${API_URL}/api/site-image-overrides`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ overrides: { [key]: url || null } }),
      });
      if (!response.ok) throw new Error('Failed to save');
      const data = await response.json();
      setSiteOverrides(data.overrides || {});
      setSiteOverrideForm((prev) => ({ ...prev, [key]: url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save override');
    } finally {
      setSiteOverridesSaving(false);
    }
  };

  // ===========================================
  // Save Functions
  // ===========================================

  // Save homepage content
  const saveContent = async (updates: Partial<HomepageContent>) => {
    if (!content) return;
    
    setContentSaving(true);
    setError(null);
    
    try {
      const response = await adminFetch(`${API_URL}/api/homepage-content`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save homepage content');
      }

      const data = await response.json();
      setContent(data);
      
      // Show success feedback
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
      successMsg.textContent = '✅ Homepage content saved!';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 3000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save homepage content');
    } finally {
      setContentSaving(false);
    }
  };

  // Handle content field change
  const handleContentChange = (field: keyof HomepageContent, value: any) => {
    if (!content) return;
    setContent({ ...content, [field]: value });
  };

  // Save content on blur (auto-save)
  const handleContentBlur = (field: keyof HomepageContent) => {
    if (!content) return;
    saveContent({ [field]: content[field] });
  };

  // ===========================================
  // Feature Functions (from HomepageFeatures)
  // ===========================================

  // Handle file upload for features
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError('Image must be smaller than 20MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await adminFetch(`${API_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      setFeatureForm({ ...featureForm, image_url: data.url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Open new feature modal
  const openNewFeatureModal = () => {
    setEditingFeatureId(null);
    setFeatureForm(emptyFeatureForm);
    setShowFeatureModal(true);
  };

  // Open edit feature modal
  const openEditFeatureModal = (feature: HomepageFeature) => {
    setEditingFeatureId(feature.id);
    setFeatureForm({
      title: feature.title,
      description: feature.description,
      image_url: feature.image_url,
      icon_name: feature.icon_name,
      is_active: feature.is_active,
      display_order: feature.display_order,
    });
    setShowFeatureModal(true);
  };

  // Save feature
  const saveFeature = async () => {
    if (!featureForm.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!featureForm.description.trim()) {
      setError('Description is required');
      return;
    }

    if (!featureForm.image_url.trim()) {
      setError('Image URL is required');
      return;
    }

    setFeatureSaving(true);
    setError(null);

    try {
      const url = editingFeatureId ? `${API_URL}/api/homepage-features/${editingFeatureId}` : `${API_URL}/api/homepage-features`;
      const method = editingFeatureId ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(featureForm),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save feature');
      }

      setShowFeatureModal(false);
      fetchFeatures();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save feature');
    } finally {
      setFeatureSaving(false);
    }
  };

  // Toggle feature active status
  const toggleFeatureActive = async (id: string) => {
    const feature = features.find(f => f.id === id);
    if (!feature) return;

    try {
      const response = await adminFetch(`${API_URL}/api/homepage-features/${id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ is_active: !feature.is_active }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      fetchFeatures();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update feature';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Delete feature
  const deleteFeature = async (id: string) => {
    if (!confirm('Delete this homepage feature? This will remove it from the "Why Choose Nanny B\'s Bakery?" section.')) return;

    try {
      const response = await adminFetch(`${API_URL}/api/homepage-features/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete');
      }
      fetchFeatures();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete feature';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // ===========================================
  // Render Functions
  // ===========================================

  // Render Hero Tab
  const renderHeroTab = () => {
    if (!content) return null;

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">Hero Section</h3>
          <p className="text-sm text-blue-800">
            The hero section is the first thing visitors see. Make it engaging and welcoming!
          </p>
        </div>

        {/* Badge Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Badge Text
          </label>
          <input
            type="text"
            value={content.hero_badge_text || ''}
            onChange={(e) => handleContentChange('hero_badge_text', e.target.value)}
            onBlur={() => handleContentBlur('hero_badge_text')}
            placeholder="e.g., Barrie's Favorite Bakery"
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
          />
          <p className="text-xs text-gray-500 mt-1">Small badge shown above the main title</p>
        </div>

        {/* Main Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Main Title *
          </label>
          <input
            type="text"
            value={content.hero_title || ''}
            onChange={(e) => handleContentChange('hero_title', e.target.value)}
            onBlur={() => handleContentBlur('hero_title')}
            placeholder="e.g., Fresh Baked Goods Made with Love"
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
          />
          <p className="text-xs text-gray-500 mt-1">Large headline on the homepage</p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            value={content.hero_description || ''}
            onChange={(e) => handleContentChange('hero_description', e.target.value)}
            onBlur={() => handleContentBlur('hero_description')}
            rows={3}
            placeholder="e.g., Visit Nanny B's Bakery in Barrie, Ontario for delicious cakes, pastries, and custom treats."
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
          />
        </div>

        <ImageSelector
          value={content.hero_image_url || ''}
          onChange={(url) => handleContentChange('hero_image_url', url)}
          label="Hero Image (storefront photo)"
          helpText="The big photo on the welcome banner. Browse, upload, or paste a URL. Click Save Hero Section when done."
          previewAspect="wide"
        />

        <label className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={content.hero_floating_icons_visible !== false}
            onChange={(e) => handleContentChange('hero_floating_icons_visible', e.target.checked)}
            className="w-5 h-5 mt-0.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-800">Show floating cookie, cake, and cupcake icons</span>
            <span className="block text-xs text-gray-600">The little animated 🍪 🎂 🧁 on the welcome banner. Uncheck to hide them.</span>
          </span>
        </label>

        <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={content.hero_floating_product_visible !== false}
            onChange={(e) => handleContentChange('hero_floating_product_visible', e.target.checked)}
            className="w-5 h-5 mt-0.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-800">Show the small floating product photo</span>
            <span className="block text-xs text-gray-600">The little card (e.g. “Our Best Seller”) on the storefront photo. Uncheck to hide it.</span>
          </span>
        </label>

        {content.hero_floating_product_visible !== false && (
          <>
            <ImageSelector
              value={content.hero_floating_product_image_url || ''}
              onChange={(url) => handleContentChange('hero_floating_product_image_url', url)}
              label="Floating product photo"
              helpText="Small photo on the welcome banner. Change it or hide the card with the checkbox above."
              previewAspect="square"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Floating product text
              </label>
              <input
                type="text"
                value={content.hero_floating_product_text || ''}
                onChange={(e) => handleContentChange('hero_floating_product_text', e.target.value)}
                onBlur={() => handleContentBlur('hero_floating_product_text')}
                placeholder="e.g., Our Best Seller! 🔥"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={() => saveContent(content)}
            disabled={contentSaving || !content.hero_title?.trim() || !content.hero_description?.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <HiSave className="w-5 h-5" />
            {contentSaving ? 'Saving...' : 'Save Hero Section'}
          </button>
        </div>
      </div>
    );
  };

  // Render Features Tab (existing functionality)
  const renderFeaturesTab = () => {
    return (
      <div className="space-y-6">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900 mb-2">Features Section</h3>
          <p className="text-sm text-purple-800">
            Manage the &quot;Why Choose Nanny B&apos;s Bakery?&quot; section with feature cards.
          </p>
        </div>

        {/* Section heading — was hardcoded on the public homepage */}
        {content && (
          <div className="bg-white rounded-xl border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Section title</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={content.features_section_visible !== false}
                  onChange={(e) => {
                    handleContentChange('features_section_visible', e.target.checked);
                    saveContent({ features_section_visible: e.target.checked });
                  }}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">Show section</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
              <input
                type="text"
                value={content.features_section_title || DEFAULT_FEATURES_SECTION.features_section_title}
                onChange={(e) => handleContentChange('features_section_title', e.target.value)}
                onBlur={() => handleContentBlur('features_section_title')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <textarea
                value={
                  content.features_section_description ||
                  DEFAULT_FEATURES_SECTION.features_section_description
                }
                onChange={(e) => handleContentChange('features_section_description', e.target.value)}
                onBlur={() => handleContentBlur('features_section_description')}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Homepage Features</h2>
            <p className="text-gray-500 text-sm mt-1">
              Edit feature cards shown in the &quot;Why Choose Nanny B&apos;s Bakery?&quot; section
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchFeatures}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Refresh"
              aria-label="Refresh features"
            >
              <HiRefresh className={`w-5 h-5 ${featuresLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={openNewFeatureModal}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <HiPlus className="w-5 h-5" />
              New Feature
            </button>
          </div>
        </div>

        {/* Features List */}
        {featuresLoading ? (
          <div className="bg-white rounded-xl border p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Loading features...</p>
          </div>
        ) : features.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature) => (
              <div
                key={feature.id}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                {/* Image Preview */}
                <div className="aspect-video rounded-lg overflow-hidden mb-4 bg-gray-100">
                  <img
                    src={feature.image_url}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/assets/images/products/placeholder.webp';
                    }}
                  />
                </div>

                {/* Title and Status */}
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      feature.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {feature.is_active ? 'Active' : 'Hidden'}
                  </span>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{feature.description}</p>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Tooltip content={feature.is_active ? 'Hide from homepage' : 'Show on homepage'}>
                    <button
                      onClick={() => toggleFeatureActive(feature.id)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title={feature.is_active ? 'Hide from homepage' : 'Show on homepage'}
                      aria-label={feature.is_active ? 'Hide from homepage' : 'Show on homepage'}
                    >
                      {feature.is_active ? (
                        <HiEyeOff className="w-5 h-5 text-gray-600" />
                      ) : (
                        <HiEye className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </Tooltip>
                  <Tooltip content="Edit feature">
                    <button
                      onClick={() => openEditFeatureModal(feature)}
                      className="p-2 rounded-lg hover:bg-purple-50 transition-colors"
                      title="Edit feature"
                      aria-label="Edit feature"
                    >
                      <HiPencil className="w-5 h-5 text-purple-600" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Delete feature">
                    <button
                      onClick={() => deleteFeature(feature.id)}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title="Delete feature"
                      aria-label="Delete feature"
                    >
                      <HiTrash className="w-5 h-5 text-red-600" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 text-left">
            <p className="text-gray-800 font-medium">No saved feature cards yet.</p>
            <p className="text-sm text-gray-600 mt-2">
              Until you add your own, the website shows three defaults: Custom Cakes, Fresh Daily (donuts photo), and Made with Love.
              Click <strong>New Feature</strong> to replace those. To remove the donuts, add your own cards (or edit Fresh Daily after it exists here).
            </p>
          </div>
        )}

        {/* Feature Modal */}
        {showFeatureModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">
                  {editingFeatureId ? 'Edit Feature' : 'New Feature'}
                </h2>
                <button
                  onClick={() => setShowFeatureModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Close"
                  aria-label="Close modal"
                >
                  <HiX className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={featureForm.title}
                    onChange={(e) => setFeatureForm({ ...featureForm, title: e.target.value })}
                    placeholder="e.g., Fresh Daily"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={featureForm.description}
                    onChange={(e) => setFeatureForm({ ...featureForm, description: e.target.value })}
                    rows={3}
                    placeholder="e.g., All our baked goods are made fresh daily..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image *
                  </label>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="feature-upload"
                    aria-label="Upload image"
                  />
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2 px-4 py-3 mb-3 border-2 border-dashed border-gray-300 rounded-lg">
                      <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-purple-600 font-medium">Uploading...</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors bg-white"
                    >
                      <HiUpload className="w-5 h-5 text-purple-600" />
                      <span className="text-gray-600">
                        <span className="text-purple-600 font-medium">Click to upload</span> an image
                      </span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowImagePicker(!showImagePicker)}
                    className="w-full px-4 py-2 mb-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700"
                  >
                    {showImagePicker ? 'Hide' : 'Browse'} Existing Images
                  </button>

                  {showImagePicker && (
                    <div className="mb-3">
                      <ImageGalleryPicker
                        isOpen={showImagePicker}
                        onClose={() => setShowImagePicker(false)}
                        onSelect={(imageUrl) => {
                          setFeatureForm(prev => ({ ...prev, image_url: imageUrl }));
                          setShowImagePicker(false);
                        }}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <span className="flex-1 border-t border-gray-200" />
                    <span>or paste URL</span>
                    <span className="flex-1 border-t border-gray-200" />
                  </div>

                  <input
                    type="text"
                    value={featureForm.image_url}
                    onChange={(e) => setFeatureForm({ ...featureForm, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon
                  </label>
                  <select
                    value={featureForm.icon_name}
                    onChange={(e) => setFeatureForm({ ...featureForm, icon_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    aria-label="Select icon"
                    title="Select icon"
                  >
                    {ICON_OPTIONS.map(icon => (
                      <option key={icon.value} value={icon.value}>{icon.label}</option>
                    ))}
                  </select>
                </div>

                {/* Display Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={featureForm.display_order}
                    onChange={(e) => setFeatureForm({ ...featureForm, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    aria-label="Display order"
                    title="Display order"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>

                {/* Active */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={featureForm.is_active}
                    onChange={(e) => setFeatureForm({ ...featureForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    title="Toggle feature active status"
                    aria-label="Active (visible on homepage)"
                  />
                  <span className="text-sm text-gray-700">Active (visible on homepage)</span>
                </label>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 p-4 border-t">
                <button
                  onClick={() => setShowFeatureModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveFeature}
                  disabled={featureSaving || !featureForm.title.trim() || !featureForm.description.trim() || !featureForm.image_url.trim()}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {featureSaving ? 'Saving...' : editingFeatureId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Sections Tab
  const renderSectionsTab = () => {
    if (!content) return null;

    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-900 mb-2">Page Sections</h3>
          <p className="text-sm text-green-800">
            Edit titles, descriptions, and visibility for each homepage section.
          </p>
        </div>

        {/* Featured Products Section */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Featured Products Section</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.featured_products_visible}
                onChange={(e) => {
                  handleContentChange('featured_products_visible', e.target.checked);
                  saveContent({ featured_products_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Visible</span>
            </label>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={content.featured_products_title || ''}
                onChange={(e) => handleContentChange('featured_products_title', e.target.value)}
                onBlur={() => handleContentBlur('featured_products_title')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={content.featured_products_description || ''}
                onChange={(e) => handleContentChange('featured_products_description', e.target.value)}
                onBlur={() => handleContentBlur('featured_products_description')}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                aria-label="Featured products section description"
                title="Featured products section description"
              />
            </div>
          </div>
        </div>

        {/* Popular Treats Section */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Popular Treats Section</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.popular_treats_visible}
                onChange={(e) => {
                  handleContentChange('popular_treats_visible', e.target.checked);
                  saveContent({ popular_treats_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Visible</span>
            </label>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={content.popular_treats_title || ''}
                onChange={(e) => handleContentChange('popular_treats_title', e.target.value)}
                onBlur={() => handleContentBlur('popular_treats_title')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={content.popular_treats_description || ''}
                onChange={(e) => handleContentChange('popular_treats_description', e.target.value)}
                onBlur={() => handleContentBlur('popular_treats_description')}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                aria-label="Popular treats section description"
                title="Popular treats section description"
              />
            </div>
          </div>
        </div>

        {/* Custom Cakes Section */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Custom Cakes Section</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.custom_cakes_visible}
                onChange={(e) => {
                  handleContentChange('custom_cakes_visible', e.target.checked);
                  saveContent({ custom_cakes_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Visible</span>
            </label>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={content.custom_cakes_title || ''}
                onChange={(e) => handleContentChange('custom_cakes_title', e.target.value)}
                onBlur={() => handleContentBlur('custom_cakes_title')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={content.custom_cakes_description || ''}
                onChange={(e) => handleContentChange('custom_cakes_description', e.target.value)}
                onBlur={() => handleContentBlur('custom_cakes_description')}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                aria-label="Custom cakes section description"
                title="Custom cakes section description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notice Text</label>
              <input
                type="text"
                value={content.custom_cakes_notice_text || ''}
                onChange={(e) => handleContentChange('custom_cakes_notice_text', e.target.value)}
                onBlur={() => handleContentBlur('custom_cakes_notice_text')}
                placeholder="e.g., 72 hours notice required for custom cakes"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
              <input
                type="text"
                value={content.custom_cakes_button_text || ''}
                onChange={(e) => handleContentChange('custom_cakes_button_text', e.target.value)}
                onBlur={() => handleContentBlur('custom_cakes_button_text')}
                placeholder="e.g., Order Custom Cake"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        {/* ── Custom Cake Pricing Section ── */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold">Custom Cake Pricing</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                When ON — price cards show on the home page. When OFF — shows "Call or Message" buttons only.
              </p>
            </div>
            {/* Master toggle — on = show prices, off = show call CTA */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.cake_pricing_visible ?? false}
                onChange={(e) => {
                  handleContentChange('cake_pricing_visible', e.target.checked);
                  saveContent({ cake_pricing_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700 font-medium">Show Pricing</span>
            </label>
          </div>

          {/* Quote copy above the prices / call buttons on the homepage */}
          <div className="mt-4 space-y-3 border-b border-gray-100 pb-4">
            <p className="text-xs text-gray-500">
              Heading shown above the prices (or Call / Message buttons). Separate from the dark Custom Cakes gallery section.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote title</label>
              <input
                type="text"
                value={content.cake_quote_title || DEFAULT_CAKE_QUOTE.cake_quote_title}
                onChange={(e) => handleContentChange('cake_quote_title', e.target.value)}
                onBlur={() => handleContentBlur('cake_quote_title')}
                placeholder={DEFAULT_CAKE_QUOTE.cake_quote_title}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote description</label>
              <textarea
                value={content.cake_quote_description || DEFAULT_CAKE_QUOTE.cake_quote_description}
                onChange={(e) => handleContentChange('cake_quote_description', e.target.value)}
                onBlur={() => handleContentBlur('cake_quote_description')}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                aria-label="Custom cake quote description"
                title="Custom cake quote description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notice banner</label>
              <input
                type="text"
                value={content.cake_quote_notice || DEFAULT_CAKE_QUOTE.cake_quote_notice}
                onChange={(e) => handleContentChange('cake_quote_notice', e.target.value)}
                onBlur={() => handleContentBlur('cake_quote_notice')}
                placeholder={DEFAULT_CAKE_QUOTE.cake_quote_notice}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
          </div>

          {/* Pricing fields — always visible so Tammy can fill them before turning on */}
          <div className="mt-4 space-y-3">
            <p className="text-xs text-gray-400 italic">
              Type whatever you want — e.g. "Starting at $65", "$55–$95", "Call for pricing", etc.
            </p>
            {[
              { field: 'cake_price_6inch' as const, label: '6" Round  (serves 8–10, single tier)' },
              { field: 'cake_price_8inch' as const, label: '8" Round  (serves 12–16, single tier)' },
              { field: 'cake_price_2tier' as const, label: '2-Tier  (serves 20–30)' },
              { field: 'cake_price_3tier' as const, label: '3-Tier  (serves 40+)' },
            ].map(({ field, label }) => (
              <div key={field} className="flex items-center gap-3">
                <label className="w-56 text-sm text-gray-700 flex-shrink-0">{label}</label>
                <input
                  type="text"
                  value={content[field] || ''}
                  onChange={(e) => handleContentChange(field, e.target.value)}
                  onBlur={() => handleContentBlur(field)}
                  placeholder='e.g. "Starting at $65"'
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Optional note below prices
              </label>
              <input
                type="text"
                value={content.cake_pricing_note || ''}
                onChange={(e) => handleContentChange('cake_pricing_note', e.target.value)}
                onBlur={() => handleContentBlur('cake_pricing_note')}
                placeholder='e.g. "Pricing varies by design complexity. Final price confirmed after consultation."'
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* ── Live Fundraisers Spotlight ── */}
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold">Live Fundraisers Section</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                When ON — active fundraiser campaigns are shown prominently on the homepage.
                When OFF — the section is hidden even if campaigns are running.
              </p>
            </div>
            {/* Toggle: show or hide the fundraiser spotlight on the homepage */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.fundraisers_visible ?? true}
                onChange={(e) => {
                  handleContentChange('fundraisers_visible', e.target.checked);
                  saveContent({ fundraisers_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700 font-medium">Show Fundraisers</span>
            </label>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Only checkout-ready campaigns (product visible + online ordering ON + within date window) appear here.
            Use the <strong>Fundraiser Campaigns</strong> page to create and manage campaigns.
          </p>
        </div>

        {/* ── Fresh from Our Kitchen ── */}
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Fresh from Our Kitchen</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Photos come from Gallery items marked Featured. Edit the heading here.
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.kitchen_gallery_visible !== false}
                onChange={(e) => {
                  handleContentChange('kitchen_gallery_visible', e.target.checked);
                  saveContent({ kitchen_gallery_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Visible</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={content.kitchen_gallery_title || DEFAULT_KITCHEN_GALLERY.kitchen_gallery_title}
              onChange={(e) => handleContentChange('kitchen_gallery_title', e.target.value)}
              onBlur={() => handleContentBlur('kitchen_gallery_title')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={
                content.kitchen_gallery_description ||
                DEFAULT_KITCHEN_GALLERY.kitchen_gallery_description
              }
              onChange={(e) => handleContentChange('kitchen_gallery_description', e.target.value)}
              onBlur={() => handleContentBlur('kitchen_gallery_description')}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button text</label>
            <input
              type="text"
              value={
                content.kitchen_gallery_button_text ||
                DEFAULT_KITCHEN_GALLERY.kitchen_gallery_button_text
              }
              onChange={(e) => handleContentChange('kitchen_gallery_button_text', e.target.value)}
              onBlur={() => handleContentBlur('kitchen_gallery_button_text')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* ── Testimonials ── */}
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Customer Testimonials</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Edit the quotes shown under &quot;What Our Customers Say&quot;.
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.testimonials_visible !== false}
                onChange={(e) => {
                  handleContentChange('testimonials_visible', e.target.checked);
                  saveContent({ testimonials_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Visible</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={content.testimonials_title || DEFAULT_TESTIMONIALS.testimonials_title}
              onChange={(e) => handleContentChange('testimonials_title', e.target.value)}
              onBlur={() => handleContentBlur('testimonials_title')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={
                content.testimonials_description || DEFAULT_TESTIMONIALS.testimonials_description
              }
              onChange={(e) => handleContentChange('testimonials_description', e.target.value)}
              onBlur={() => handleContentBlur('testimonials_description')}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="space-y-3">
            {normalizeTestimonialsItems(content.testimonials_items).map((item, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Quote {index + 1}
                  </p>
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => {
                      const next = normalizeTestimonialsItems(content.testimonials_items).filter(
                        (_, i) => i !== index
                      );
                      handleContentChange('testimonials_items', next);
                      saveContent({ testimonials_items: next });
                    }}
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={item.name}
                    placeholder="Name"
                    onChange={(e) => {
                      const next = normalizeTestimonialsItems(content.testimonials_items).map(
                        (row, i) => (i === index ? { ...row, name: e.target.value } : row)
                      );
                      handleContentChange('testimonials_items', next);
                    }}
                    onBlur={() =>
                      saveContent({
                        testimonials_items: normalizeTestimonialsItems(content.testimonials_items),
                      })
                    }
                    className="w-full px-3 py-1.5 border rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    value={item.occasion}
                    placeholder="Occasion"
                    onChange={(e) => {
                      const next = normalizeTestimonialsItems(content.testimonials_items).map(
                        (row, i) => (i === index ? { ...row, occasion: e.target.value } : row)
                      );
                      handleContentChange('testimonials_items', next);
                    }}
                    onBlur={() =>
                      saveContent({
                        testimonials_items: normalizeTestimonialsItems(content.testimonials_items),
                      })
                    }
                    className="w-full px-3 py-1.5 border rounded-lg text-sm"
                  />
                </div>
                <textarea
                  value={item.text}
                  placeholder="Quote"
                  rows={2}
                  onChange={(e) => {
                    const next = normalizeTestimonialsItems(content.testimonials_items).map(
                      (row, i) => (i === index ? { ...row, text: e.target.value } : row)
                    );
                    handleContentChange('testimonials_items', next);
                  }}
                  onBlur={() =>
                    saveContent({
                      testimonials_items: normalizeTestimonialsItems(content.testimonials_items),
                    })
                  }
                  className="w-full px-3 py-1.5 border rounded-lg text-sm"
                />
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-purple-700 font-medium hover:underline"
              onClick={() => {
                const next = [
                  ...normalizeTestimonialsItems(content.testimonials_items),
                  { name: 'Customer', rating: 5, text: 'Add your quote here.', occasion: 'Order' },
                ];
                handleContentChange('testimonials_items', next);
                saveContent({ testimonials_items: next });
              }}
            >
              + Add quote
            </button>
          </div>
        </div>

        {/* ── Social Proof Counter Bar ── */}
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Social Proof Counter Bar</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                4 stat boxes (cakes made, rating, etc.) shown above the CTA. Builds buyer trust.
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.social_proof_visible ?? false}
                onChange={(e) => {
                  handleContentChange('social_proof_visible', e.target.checked);
                  saveContent({ social_proof_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700 font-medium">Show Stats Bar</span>
            </label>
          </div>

          {/* 4 stat pairs — each has a big number + a label */}
          <div className="grid grid-cols-2 gap-4">
            {(
              [
                { n: 1, numKey: 'social_proof_stat_1_number', labelKey: 'social_proof_stat_1_label' },
                { n: 2, numKey: 'social_proof_stat_2_number', labelKey: 'social_proof_stat_2_label' },
                { n: 3, numKey: 'social_proof_stat_3_number', labelKey: 'social_proof_stat_3_label' },
                { n: 4, numKey: 'social_proof_stat_4_number', labelKey: 'social_proof_stat_4_label' },
              ] as const
            ).map(({ n, numKey, labelKey }) => (
              <div key={n} className="space-y-2 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Stat {n}</p>
                <div>
                  <label className="block text-xs text-gray-500 mb-1" htmlFor={`stat-${n}-num`}>Number / Value</label>
                  <input
                    id={`stat-${n}-num`}
                    type="text"
                    value={content[numKey] || ''}
                    onChange={(e) => handleContentChange(numKey, e.target.value)}
                    onBlur={() => handleContentBlur(numKey)}
                    placeholder="e.g. 500+"
                    className="w-full px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1" htmlFor={`stat-${n}-label`}>Label</label>
                  <input
                    id={`stat-${n}-label`}
                    type="text"
                    value={content[labelKey] || ''}
                    onChange={(e) => handleContentChange(labelKey, e.target.value)}
                    onBlur={() => handleContentBlur(labelKey)}
                    placeholder="e.g. Cakes Made"
                    className="w-full px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render CTAs Tab
  const renderCTAsTab = () => {
    if (!content) return null;

    return (
      <div className="space-y-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h3 className="font-semibold text-orange-900 mb-2">Call-to-Action Section</h3>
          <p className="text-sm text-orange-800">
            The &quot;Ready to Order?&quot; section at the bottom of the homepage.
          </p>
        </div>

        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">CTA Section</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={content.cta_section_visible}
                onChange={(e) => {
                  handleContentChange('cta_section_visible', e.target.checked);
                  saveContent({ cta_section_visible: e.target.checked });
                }}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Visible</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={content.cta_section_title || ''}
              onChange={(e) => handleContentChange('cta_section_title', e.target.value)}
              onBlur={() => handleContentBlur('cta_section_title')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={content.cta_section_description || ''}
              onChange={(e) => handleContentChange('cta_section_description', e.target.value)}
              onBlur={() => handleContentBlur('cta_section_description')}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              aria-label="CTA section description"
              title="CTA section description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order Button Text</label>
            <input
              type="text"
              value={content.cta_button_text || ''}
              onChange={(e) => handleContentChange('cta_button_text', e.target.value)}
              onBlur={() => handleContentBlur('cta_button_text')}
              placeholder="e.g., Order Online Now"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Button Text</label>
            <input
              type="text"
              value={content.cta_phone_button_text || ''}
              onChange={(e) => handleContentChange('cta_phone_button_text', e.target.value)}
              onBlur={() => handleContentBlur('cta_phone_button_text')}
              placeholder="e.g., Call (705) 503-4966"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Note: Phone number comes from Settings page</p>
          </div>
        </div>
      </div>
    );
  };

  // Render SEO Tab
  const renderSEOTab = () => {
    if (!content) return null;

    return (
      <div className="space-y-6">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h3 className="font-semibold text-indigo-900 mb-2">SEO (Search Engine Optimization)</h3>
          <p className="text-sm text-indigo-800">
            Help search engines understand your homepage. These fields help with Google rankings.
          </p>
        </div>

        <div className="bg-white rounded-xl border p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meta Title
            </label>
            <input
              type="text"
              value={content.meta_title || ''}
              onChange={(e) => handleContentChange('meta_title', e.target.value)}
              onBlur={() => handleContentBlur('meta_title')}
              placeholder="Nanny B's Bakery | Custom Cakes & Fresh Pastries in Barrie, ON"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown in search results (50-60 characters recommended). 
              <span className="text-purple-600 font-medium"> Current: {content.meta_title?.length || 0} characters</span>
            </p>
            {content.meta_title && content.meta_title.length > 60 && (
              <p className="text-xs text-amber-600 mt-1">⚠️ Title is longer than recommended (60 chars). Google may truncate it.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meta Description
            </label>
            <textarea
              value={content.meta_description || ''}
              onChange={(e) => handleContentChange('meta_description', e.target.value)}
              onBlur={() => handleContentBlur('meta_description')}
              rows={3}
              placeholder="Visit Nanny B's Bakery in Barrie, Ontario for delicious custom cakes, fresh pastries, and baked goods. Specializing in birthday cakes, wedding cakes, and special occasion treats. Order online or visit us today!"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              aria-label="Meta description for search engines"
              title="Meta description for search engines"
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown in search results (150-160 characters recommended). 
              <span className="text-purple-600 font-medium"> Current: {content.meta_description?.length || 0} characters</span>
            </p>
            {content.meta_description && content.meta_description.length > 160 && (
              <p className="text-xs text-amber-600 mt-1">⚠️ Description is longer than recommended (160 chars). Google may truncate it.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meta Keywords (comma-separated)
            </label>
            <input
              type="text"
              value={content.meta_keywords || ''}
              onChange={(e) => handleContentChange('meta_keywords', e.target.value)}
              onBlur={() => handleContentBlur('meta_keywords')}
              placeholder="bakery Barrie, custom cakes Barrie, birthday cakes Barrie, wedding cakes Barrie, fresh pastries Barrie, bakery Ontario, custom cake bakery, Nanny B's Bakery, Barrie bakery, fresh baked goods Barrie"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Keywords separated by commas. Include location (Barrie, Ontario) and services (custom cakes, pastries, etc.)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Open Graph Image (for social media)
            </label>
            <input
              type="text"
              value={content.og_image_url || ''}
              onChange={(e) => handleContentChange('og_image_url', e.target.value)}
              onBlur={() => handleContentBlur('og_image_url')}
              placeholder="/assets/images/exterior/storefront-exterior.webp"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Image shown when homepage is shared on Facebook, Twitter, LinkedIn, etc. 
              Recommended size: 1200x630px. Use your storefront or best product image.
            </p>
            {content.og_image_url && (
              <div className="mt-2 bg-gray-100 rounded-lg p-2">
                <img
                  src={content.og_image_url}
                  alt="OG image preview"
                  className="w-full h-48 object-cover rounded"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Site Images tab: optional overrides for all hardcoded images (logo, hero, placeholders, feature cards, schema)
  const renderSiteImagesTab = () => {
    if (siteOverridesLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full" />
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold text-amber-900 mb-2">Site image overrides</h3>
          <p className="text-sm text-amber-800">
            Set an override URL for any image below to use it instead of the default. Leave override empty to use the default. This does not remove defaults—you can clear an override anytime.
          </p>
        </div>
        <div className="space-y-4">
          {SITE_IMAGE_KEYS.map(({ key, label, defaultUrl }) => (
            <div key={key} className="border border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <p className="text-xs text-gray-500 mb-2">Default: {defaultUrl}</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={siteOverrideForm[key] ?? ''}
                  onChange={(e) => setSiteOverrideForm((prev) => ({ ...prev, [key]: e.target.value }))}
                  onBlur={() => {
                    const value = (siteOverrideForm[key] ?? '').trim();
                    if (value !== (siteOverrides[key] ?? '')) {
                      saveSiteOverride(key, value);
                    }
                  }}
                  placeholder="Override URL (leave empty for default)"
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    setSiteOverrideForm((prev) => ({ ...prev, [key]: '' }));
                    saveSiteOverride(key, '');
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Clear
                </button>
              </div>
              {(siteOverrideForm[key] ?? '').trim() && (
                <div className="mt-2 bg-gray-100 rounded-lg p-2">
                  <img
                    src={(siteOverrideForm[key] ?? '').trim()}
                    alt="Preview"
                    className="max-h-24 object-contain rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {siteOverridesSaving && (
          <p className="text-sm text-gray-500">Saving...</p>
        )}
      </div>
    );
  };

  // ===========================================
  // Main Render
  // ===========================================

  if (contentLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Loading homepage editor...</p>
        </div>
      </div>
    );
  }

  const tabs: Array<{ id: TabType; label: string; icon: typeof HiPhotograph }> = [
    { id: 'hero', label: 'Hero Section', icon: HiPhotograph },
    { id: 'features', label: 'Features', icon: HiSparkles },
    { id: 'sections', label: 'Sections', icon: HiDocumentText },
    { id: 'ctas', label: 'CTAs', icon: HiTag },
    { id: 'seo', label: 'SEO', icon: HiGlobe },
    { id: 'site-images', label: 'Site Images', icon: HiPhotograph },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Homepage Editor</h1>
          <p className="text-gray-500 mt-1">
            Edit all homepage content - hero, sections, features, and more
          </p>
        </div>
        <button
          onClick={fetchContent}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          title="Refresh"
          aria-label="Refresh content"
        >
          <HiRefresh className={`w-5 h-5 ${contentLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${isActive
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border p-6">
        {activeTab === 'hero' && renderHeroTab()}
        {activeTab === 'features' && renderFeaturesTab()}
        {activeTab === 'sections' && renderSectionsTab()}
        {activeTab === 'ctas' && renderCTAsTab()}
        {activeTab === 'seo' && renderSEOTab()}
        {activeTab === 'site-images' && renderSiteImagesTab()}
      </div>
    </div>
  );
}
